import { useEffect, useState } from "react";
import { Pencil, Trash2, RefreshCw, Clock } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { apiListOrders, apiDeleteOrder } from "../lib/api";
import { formatINR } from "../lib/menu";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function OrderHistory({ refreshKey, onEdit }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiListOrders();
      setOrders(res.orders || []);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteOrder(toDelete.id);
      toast.success("Order deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const filtered = orders.filter((o) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      o.employee?.toLowerCase().includes(f) ||
      o.payment_method?.toLowerCase().includes(f) ||
      o.custom_payment?.toLowerCase().includes(f) ||
      o.items?.some((it) => it.name?.toLowerCase().includes(f))
    );
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-[#1A1918] font-heading">
            Order History
          </h2>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854] mt-1">
            {orders.length} total orders
          </p>
        </div>
        <div className="flex items-end gap-3">
          <input
            placeholder="Filter by item, employee, payment..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="orders-filter-input"
            className="bg-white border border-[#E6E2D8] rounded-md px-3 py-2 text-base focus:border-[#C84C31] focus:ring-2 focus:ring-[#C84C31]/20 outline-none w-72"
          />
          <Button
            variant="outline"
            onClick={load}
            data-testid="orders-refresh-button"
            className="border-[#E6E2D8] text-[#1A1918] hover:bg-[#F5F2EB] h-10"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <div
        data-testid="order-history-table"
        className="w-full overflow-x-auto rounded-lg border border-[#E6E2D8] bg-[#FFFCF5]"
      >
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F5F2EB] border-b border-[#E6E2D8]">
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                When
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                Employee
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                Items
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">
                Payment
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6 text-right">
                Total
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6 text-right">
                Paid
              </th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-[#5C5854] text-sm">
                  {loading ? "Loading..." : "No orders found."}
                </td>
              </tr>
            ) : (
              filtered.map((o) => {
                const paid = Number(o.paid ?? o.total ?? 0);
                const total = Number(o.total ?? 0);
                const diff = paid - total;
                const isOwner = o.payment_method === "Owner";
                return (
                <tr
                  key={o.id}
                  className="border-b border-[#E6E2D8] last:border-0 hover:bg-[#F5F2EB]/50"
                  data-testid={`order-row-${o.id}`}
                >
                  <td className="py-4 px-6 text-sm text-[#1A1918] align-top">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-[#5C5854]" />
                      {fmtDate(o.timestamp)}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm align-top">
                    <div className="font-semibold text-[#1A1918]">{o.employee}</div>
                    {o.title && (
                      <div className="text-xs text-[#5C5854]">{o.title}</div>
                    )}
                  </td>
                  <td className="py-4 px-6 text-sm align-top max-w-md">
                    <ul className="space-y-0.5">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="text-[#1A1918]">
                          <span className="font-semibold">{it.qty}×</span> {it.name}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-4 px-6 text-sm align-top">
                    <span className="inline-block bg-[#F5F2EB] border border-[#E6E2D8] rounded-md px-2.5 py-1 text-xs font-bold">
                      {o.payment_method}
                      {o.payment_method === "Custom" && o.custom_payment
                        ? `: ${o.custom_payment}`
                        : ""}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-base text-right font-black tabular-nums text-[#1A1918] align-top font-heading">
                    {formatINR(o.total)}
                  </td>
                  <td
                    className="py-4 px-6 text-base text-right font-bold tabular-nums align-top"
                    data-testid={`order-paid-${o.id}`}
                  >
                    {isOwner ? (
                      <span className="text-[#5C5854] italic font-normal text-sm">comped</span>
                    ) : (
                      <>
                        <div
                          className={
                            diff < 0
                              ? "text-[#F44336]"
                              : diff > 0
                              ? "text-[#5B7059]"
                              : "text-[#1A1918]"
                          }
                        >
                          {formatINR(paid)}
                        </div>
                        {diff < 0 && (
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#F44336] mt-0.5">
                            Due {formatINR(Math.abs(diff))}
                          </div>
                        )}
                        {diff > 0 && (
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#5B7059] mt-0.5">
                            +{formatINR(diff)} change
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="py-4 px-6 align-top">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => onEdit?.(o)}
                        data-testid={`edit-order-${o.id}`}
                        className="text-[#5C5854] hover:text-[#C84C31] transition-colors"
                        aria-label="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setToDelete(o)}
                        data-testid={`delete-order-${o.id}`}
                        className="text-[#5C5854] hover:text-[#F44336] transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#FFFCF5] border-[#E6E2D8]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the order from the Google Sheet. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm"
              className="bg-[#F44336] hover:bg-[#D32F2F] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
